'use strict';

define('vb/extensions/dynamic/private/helpers/serviceMetadataProviderHelper',[
  'vb/private/utils',
  'vb/private/log',
  'vb/private/services/endpointReferenceFactory',
  'vb/private/services/servicesManager',
  'vb/extensions/dynamic/private/helpers/dataDescriptionMetadataProviderHelper',
  'vb/private/constants',
  'vb/private/stateManagement/application',
  'vb/extensions/dynamic/private/models/layoutResource',
  'vb/extensions/dynamic/private/models/serviceMetadata',
], (
  Utils,
  Log,
  EndpointReferenceFactory,
  ServicesManager,
  DataDescriptionMetadataProviderHelper,
  Constants,
  Application,
  LayoutResource,
  ServiceMetadata,
) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/layout').default} Layout
   * @typedef {import('vb/extensions/dynamic/private/helpers/layoutSerializer').default} LayoutSerializer
   * @typedef {import('vb/extensions/dynamic/private/helpers/layoutDeserializer').default} LayoutDeserializer
   */

  const logger = Log.getLogger('/vb/extensions/dynamic/private/helpers/serviceMetadataProviderHelper');
  /**
   * ServiceMetadataProviderHelper can be used as the "vbHelper" interface that is passed to
   * JET dynamic UI metadata provider for interfacing with VB.
   *
   * This extends DataDescriptionMetadataProviderHelper, and implements the openapi3-based version of
   * the helper; the metadata comes from VB service metadata, rather than a local JSON file.
   *
   * This contains one additional function, loadExternalServiceMetadata, which is used by JET, but is deprecated.
   *
   * The very early dynamic UI/VB integration required some workarounds to enable its use; that functionality
   * is all encapsulated in the class, and should eventually be deprecated/removed.
   *
   * @see DataDescriptorMetadataProviderHelper for the closely-related JSON-based version.
   *
   * its 'private', but shared with JET; not for use my application developers
   *
   * @extends DataDescriptionMetadataProviderHelper
   */
  class ServiceMetadataProviderHelper extends DataDescriptionMetadataProviderHelper {
    /**
     * Retrieves an instance of the ServiceMetadataProviderHelper class.
     *
     * @static
     * @param {object} options Configuration options for the helper and provider.
     * @param {string} options.id - Unique identifier for the helper.
     * @param {string} options.endpoint - Endpoint.
     * @param {*} vbAppContext Context created by VB, passed as 'options.context' to components/providers.
     * @param {Container} container VB container where the dynamic component with this layout will be used.
     * @returns {Promise<ServiceMetadataProviderHelper>} A promise resolving to an instance of
     *                                                   ServiceMetadataProviderHelper.
     */
    static get(options, vbAppContext, container) {
      const endpointId = options.endpoint;
      // get the base configuration
      const helperOptions = ServiceMetadataProviderHelper.getHelperConfiguration();

      // the 'metadata' is unused, because we override the 'fetch' method.
      delete helperOptions.descriptors.metadata;

      // add the endpointId to the existing options
      const opts = Object.assign({
        endpointId,
      }, options, { helper: helperOptions });

      return new ServiceMetadataProviderHelper(opts, vbAppContext, container).init();
    }

    /**
     * Retrieves a resolved endpoint reference using the provided options and container.
     *
     * @param {Object} options - Configuration options for the helper.
     * @param {string} options.id - Unique identifier for the helper.
     * @param {string} options.endpoint - Endpoint.
     * @param {string} [options.endpointId] - The identifier of the endpoint to resolve.
     * @param {string} [options.extension] - Extension used to resolve the endpoint and layouts.
     * @param {string} [options.root] - Root path for loading layouts (only supported for V1 extension apps).
     * @param {string} [options.layoutPath] - Optional override for layout path.
     * @param {string} options.layoutType - Type of layout being used.
     * @param {LayoutSerializer} [options.serializer] - Serializer instance for serializing layout data.
     * @param {LayoutDeserializer} [options.deserializer] - Deserializer instance for deserializing layout data.
     * @param {Object} options.helper - Helper configuration object.
     * @param {Object} options.helper.descriptors - Descriptors for loading resources.
     * @param {Object} options.helper.descriptors.metadata - Metadata descriptor.
     * @param {Array<Object>} options.helper.descriptors.file - File descriptors.
     * @param {Container} container - The container used to resolve the endpoint reference.
     * @returns {*} The resolved endpoint reference.
     */
    static getEndpointReference(options, container) {
      return EndpointReferenceFactory.getResolvedReference(options.endpointId, container);
    }

    /**
     * Retrieves and customizes the helper configuration for service metadata providers.
     *
     * This method obtains the base configuration from `DataDescriptionMetadataProviderHelper.getHelperConfiguration()`,
     * then replaces any descriptor with a `resourceName` of `'data-description'` with a new descriptor
     * for `'data-description-overlay'`, including additional properties such as `altExtensionResourceName`,
     * `propertyMap`, `modelClass`, `defaultBundleResourceName`, and `skipTemplate`.
     *
     * @returns {Object} The customized helper configuration object with updated descriptors.
     */
    static getHelperConfiguration() {
      // get the base configuration
      const helperOptions = DataDescriptionMetadataProviderHelper.getHelperConfiguration();

      // remove data-description resource descriptor so we can replace it with data-description-overlay
      helperOptions.descriptors = helperOptions.descriptors
        .map((desc) => {
          if (desc.resourceName === 'data-description') {
            return {
              resourceName: 'data-description-overlay',
              altExtensionResourceName: 'data-description',
              propertyMap: {
                descriptor: 'clientMetadata',
                viewModel: 'clientMetadataModel',
              },
              modelClass: LayoutResource,
              defaultBundleResourceName: Constants.DefaultBundleResourceNames.DYNAMIC_LAYOUTS,
              skipTemplate: true,
            };
          }

          return desc;
        });

      return helperOptions;
    }

    /**
     * Creates an instance of the class.
     * @param {Object} options - Configuration options for the service metadata provider.
     * @param {Object} vbAppContext - The Visual Builder application context.
     * @param {Container} container - The container for dependency injection or service management.
     */
    constructor(options, vbAppContext, container) {
      super(options, vbAppContext, container);

      this.fetchServiceInfoPromise = null;
    }

    /**
     * Initializes the service metadata provider helper instance.
     *
     * - Sets the `endpointId` from options or defaults to an empty string.
     * - Retrieves and sets the `endpointReference` using the provided options and container.
     * - If the `endpointReference` contains a namespace and no explicit extension is configured,
     *   sets the `extensionId` accordingly.
     * - Calls the parent class's `init` method.
     * - If a `layoutPath` is provided in options, constructs the full layout path and sets it.
     *
     * @async
     * @returns {Promise<ServiceMetadataProviderHelper>} The initialized instance.
     */
    async init() {
      this.endpointId = this.options.endpointId || ''; // empty string should never happen
      if (this.options.extension) {
        // The extension option identifies where BOSS dynamic layout artifacts should be loaded from.
        this.extensionId = this.options.extension;
        if (this.extensionId !== Constants.ExtensionFolders.BASE
          && !Application.extensionRegistry.getExtensionById(this.extensionId)) {
          // Populate the registry before deciding whether this should use a real or synthetic extension.
          await Application.extensionRegistry.getExtensions();
        }
      }
      this.endpointReference = await ServiceMetadataProviderHelper.getEndpointReference(this.options, this.container);

      // If the endpointReference has a namespace, preserve the legacy behavior of using
      // that namespace as the layout owner only when no explicit layout extension is
      // configured. The extension option identifies where BOSS dynamic layout artifacts
      // should be loaded from, while the endpoint namespace identifies where the service
      // endpoint is resolved from.
      const namespace = this.endpointReference.namespace;
      if (!this.options.extension && namespace) {
        this.extensionId = namespace;
      }

      await super.init();

      const descriptorClassName = this.options.metadataDescriptor
        && this.options.metadataDescriptor.constructor
        && this.options.metadataDescriptor.constructor.name;
      if (namespace && descriptorClassName === 'ServiceMetadataProviderDescriptor') {
        // Service metadata providers load imports from service metadata, not dynamic layout files.
        // Keep their resource root on the caller container even when the endpoint is namespaced.
        this.extension = this.container && this.container.extension;
        this.setLayoutRoot(Constants.DefaultPaths.LAYOUTS);
      }

      const { layoutPath } = this.options;

      if (layoutPath) {
        const path = Utils.addTrailingSlash(`${this.getLayoutRoot()}${layoutPath}`);
        this.setLayoutPath(path);
      }

      return this;
    }

    /**
     * Overridden to return true if the endpoint is a BOSS endpoint.
     *
     * @returns {boolean}
     */
    get isRunnableOnServer() {
      // TODO: this should be provided by service type metadata (which we do not have at the moment)
      // Only a BOSS endpoint will have a module name.
      return (!!this.endpointReference.moduleName && this.endpointReference.serviceType === 'boss');
    }

    /**
     * Used by JET to get an unique url for caching metadata. To guarantee the url is unique,
     * it will be constructed based on the extension id, the container's full path and
     * the id of the metadata descriptor variable.
     *
     * @returns {Promise<String>}
     */
    toUrl() {
      return Promise.resolve()
        .then(() => `${this.extension.id}/${this.container.fullPath}/${this.id}`);
    }

    /**
     * Returns a stable cache key for the underlying service metadata.
     *
     * This key identifies the resolved service definition, not the helper instance.
     * It is stable across fragment instances and matches the identity VB runtime
     * uses internally for service-definition caching.
     *
     * @returns {Promise<string|null>} Canonical cache key for the service metadata, or null if the service cannot be resolved.
     */
    getKey() {
      return ServicesManager.getServiceCacheKey(this.endpointReference);
    }

    /**
     * Returns the metadata in JSON format.
     *
     * Note: This method replaces the deprecated fetch method and is more efficient because it does not
     * stringify the service definition and wrap it in a Response object.
     *
     * @returns {Promise<Object>} A promise that resolves with the processed layout resources
     *                            containing the service metadata and loaded imports
     * @see ConfigurableMetadataProviderHelper.getMetadata
     */
    // Synthetic BOSS extensions may be missing the object context that JET expects
    // for server-runnable layouts, so add an empty object context when the context
    // descriptor is part of the loaded resource set.
    loadLayoutResources(resourceLoc, descriptors) {
      return super.loadLayoutResources(resourceLoc, descriptors)
        .then((resourceMap) => {
          if (this.isRunnableOnServer && this.extension && this.extension.synthetic
            && descriptors.some((descriptor) => descriptor.resourceName === 'context')) {
            let context;
            try {
              context = resourceMap.contextMetadata && JSON.parse(resourceMap.contextMetadata);
            } catch (e) {
              context = {};
            }
            if (!context || !context.objectContext) {
              resourceMap.contextMetadata = JSON.stringify(Object.assign({}, context, { objectContext: {} }));
            }
          }
          return resourceMap;
        });
    }

    getMetadata() {
      return this.fetchDirect(true)
        .then((fetchedInfo) => {
          // descriptor used to process imports in the info.x-vb section of the service metadata
          const descriptors = [{
            resourceName: '',
            propertyMap: {
              descriptor: 'data',
              viewModel: 'dataModel',
              headers: 'headers',
            },
            modelClass: ServiceMetadata,
            metadata: fetchedInfo.data,
            headers: fetchedInfo.headers,

            // only need to load imports so skip everything else
            skipTemplate: true,
            skipFunctions: true,
            skipBundles: true,
            skipExtensions: true,
          }];

          // use ui/self as the resource path for V2 extension
          const resourcePath = (this.extension.id !== Constants.ExtensionFolders.BASE)
            ? `${Constants.DefaultPaths.UI}${Constants.ExtensionFolders.SELF}/`
            : '';

          return this.loadLayoutResources(resourcePath, descriptors);
        });
    }

    /**
     * Returns additional metadata in JSON format.
     *
     * Note: This method returns the service definition object which is being
     * shared with the VBRT services. Using it avoids extra network fetching and parsing
     * of the returned string.
     *
     * @param {string} metadataUrl - The URL to fetch the service metadata from.
     * @returns {Promise<Object>|null} A promise that resolves with the service definition object containing
     *                                 the service definition data and headers, or null if not found.
     * @see ConfigurableMetadataProviderHelper.getExternalMetadata
     */
    async getExternalMetadata(metadataUrl) {
      // serviceContext, serverVariables);
      const def = await ServicesManager.FindDefinitionByUrl(metadataUrl, this.container);
      return def && ({ data: await def.service.getDefinition(), headers: def.service.headers });
    }

    /**
     * Returns details describing the endpoint reference.
     * Returned object has this properties:
     *  serviceDef - JSON Object containing service definition model that includes referenced endpoint
     *  requestInit - configuration for the fetch() Request object used to get the definition
     * @returns {Promise<{serviceDef, service, configInfo, requestInit, endpoint}>}
     * @private
     */
    fetchServiceInfo() {
      if (!this.fetchServiceInfoPromise) {
        // Get all necessary information about the referenced endpoint
        // (VBS-37847) To preserve old behavior we disable accessibility validation while loading service metadata
        this.fetchServiceInfoPromise = ServicesManager.getDefinitionInfo(this.endpointReference, false);
      }
      return this.fetchServiceInfoPromise;
    }

    /**
     * 'direct' means, return the service definition already referenced by the application.
     * The service is loaded using the normal service definition loading mechanisms, instead of Rest helper fetch().
     * @param {Boolean} returnJson If true, return the service definition in JSON format. Otherwise, return
     * the service definition via a Response object.
     * @returns {Promise.<Response|Object>}
     * @private
     */
    fetchDirect(returnJson) {
      return this.fetchServiceInfo()
        .then((info) => {
          if (info && info.serviceDef) {
            const { serviceDef, service } = info;

            if (returnJson) {
              const headers = (service && service.headers) || new Headers();
              return { data: serviceDef, headers };
            }

            const s = JSON.stringify(serviceDef);
            return new Response(s);
          }

          throw new Error(`unable to load service definition: ${this.endpointReference.serviceId}`);
        });
    }

    /**
     * Allows override of the default '../../dynamicLayouts/<service>/<path>/layout.json' layout path
     * This also overrides the layoutRoot.
     * @param path
     * @returns {ServiceMetadataProviderHelper}
     */
    setLayoutPath(path) {
      this.layoutPathOverride = path;
      return this;
    }

    /**
     * Gets the path for the given operationId, removes all the parameters, and uses that as the path.
     * examples: /foo/{foo_id}/bar/{bar_id} => foo/bar
     *
     * @returns {Promise<string>} A promise that resolves to the normalized layout path prefix.
     * @see ConfigurableMetadataProviderHelper.calcLayoutPath
     */
    calcLayoutPath() {
      if (!this.layoutPrefixPromise) {
        if (this.layoutPathOverride) {
          // getLayoutPrefix used to be getLayoutPath; the override isn't ever used, but just in case it is,
          // interpret it as a path prefix, and strip the layout.json
          let prefixOverride = this.layoutPathOverride;
          const index = prefixOverride.indexOf('layout.json');
          if (index > -1) {
            prefixOverride = prefixOverride.substring(0, index);
          }
          this.layoutPrefixPromise = Promise.resolve(prefixOverride);
        } else if (!this.endpointReference.operationId) {
          // fallback, but this will never happen. do something reasonable if it dos, for some weird reason.
          logger.warn('no operationId, using empty path');
          this.layoutPrefixPromise = Promise.resolve(this.normalizePath(''));
        } else {
          this.layoutPrefixPromise = this.fetchServiceInfo()
            .then((serviceInfo) => {
              const endpoint = serviceInfo && serviceInfo.endpoint;
              const path = endpoint && this.normalizePath(endpoint.path);
              if (!path) {
                logger.error('unable to find operationId for layout prefix:', this.endpointReference.toString());
              }
              return path;
            });
        }
      }
      return this.layoutPrefixPromise;
    }

    /**
     * Provides a way to vbHelper clients to follow external OpenAPI links (operationRef, x-links, etc),
     * using the same headers/proxy/etc that  was used to fetch the original service definition.
     *
     * @param {string} url - The URL to load the service metadata from.
     * @returns {Promise<Object>} JSON Object
     * @throws {Error} If the URL is invalid or the fetch fails.
     * @deprecated JET components should do the fetch directly, and VB will intercept using UrlMapper
     */
    loadExternalServiceMetadata(url) {
      // use the original behavior if mapping is disabled
      logger.warn('ServiceMetadataProviderHelper.loadExternalServiceMetadata is DEPRECATED! ',
        'It will be removed in a future release.');
      if (this.urlMapperDisabled) {
        let requestInit;
        return Promise.resolve()
          .then(() => {
            if (!url) {
              throw new Error('invalid URL passed to loadExternalServiceMetadata.');
            }
            return this.fetchServiceInfo();
          })
          .then((info) => {
            requestInit = info.requestInit;
            return Utils.getRuntimeEnvironment();
          })
          .then((env) => env.getServiceDefinition(url, requestInit))
          .catch((e) => {
            logger.error('failed to load serviceDefinition for metadata url', url, e);
            throw e; // rethrow
          });
      }

      // just do the fetch using the URL
      return fetch(url)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }

          logger.error('failed to load serviceDefinition for metadata url', url, response.status);
          throw Error(`unable to load service metadata: status: ${response.status}`);
        });
    }

    /**
     * For a non-BOSS endpoint, remove parameters from the path, includes ending slash, e.g.,
     * dynamicLayouts/insidersales/
     *
     * For a BOSS endpoint, strip off $query and prepend modules/moduleName to the path.
     * For example, given a BOSS endpoint, boss#invoiceModule/List_invoices, and the path,
     * /invoices/$query, the normalized path would be dynamicLayout/self/modules/invoiceModule/invoices/.
     *
     * @param {string} [path=''] - The API path to normalize.
     * @returns {string} The normalized path, formatted for layout and endpoint requirements.
     */
    normalizePath(path = '') {
      // Remove parameters in { }
      const parts = path.split('/');
      let newPath = parts.filter((part) => part && part[0] !== '{').join('/');

      // BOSS endpoint only, e.g, boss#invoiceModule/List_invoices
      // Note: Only a BOSS endpoint will have a module name.
      const isBoss = !!this.endpointReference.moduleName;
      if (isBoss) {
        newPath = `${Constants.DefaultPaths.MODULES}${this.getModulePath()}/${this.getObjectPath()}`;
      }

      const operationPart = Utils.addTrailingSlash(newPath);
      // don't add the service ID, because elastic search and data services may need to share layouts
      // return `${this.layoutRootPrefix}${this.serviceId}${Constants.PATH_SEPARATOR}${operationPart}`;
      return `${this.getLayoutRoot()}${operationPart}`;
    }

    /**
     * Gets the module path by converting the endpoint reference's module name to camelCase.
     * @returns {string} The module name converted to camelCase format.
     */
    getModulePath() {
      const moduleName = this.endpointReference.moduleName;
      return Utils.pathToCamelCase(moduleName);
    }

    /**
     * Extracts the object name from the endpoint reference.
     *
     * Parses the endpoint name by splitting on '/' and taking the first segment,
     * or falls back to the operationId if endpoint name is not available.
     * If the resulting name contains underscores, extracts the second part
     * (assuming format: <action>_<obj>_<child>_<child>...).
     *
     * @returns {string} The extracted object name, or an empty string if not found.
     */
    getObjectPath() {
      // this is not working as the path is not relative to the module endpoint
      //    "/invoices/$query".
      // Instead it is:
      //    "/data/objects/ora/labsRedwoodRef/invoices/v1/$en/invoices/$query"
      // It contains data prefix "data/objects", module ID "ora/labsRedwoodRef/invoices", and more.

      // strip off $query and everything after
      // const index = newPath.indexOf('$query');

      const endpointName = this.endpointReference.endpointName || '';
      let objName = endpointName.split('/')[0] || this.endpointReference.operationId;
      if (objName) {
        const parts = objName.split('_');
        // parts are <action>_<obj>_<child>_<child>...
        if (parts.length > 1) {
          objName = parts[1];
        }
      }
      return objName;
    }

    /**
     * Creates an instance of the given modelClass.
     * Overrides base implementation in ConfigurableMetadataProviderHelper, to add moduleName for building
     * translations bundles.
     *
     * @param {Function} modelClass - The class constructor for the model to be created
     * @param {string} resourceLoc - The resource location path where the model is defined
     * @param {Object} descriptor - The model descriptor configuration object
     * @param {boolean} [descriptor.skipBundles=false] - Whether to skip bundle configuration
     * @param {string} descriptor.defaultBundleResourceName - The default bundle resource name
     * @returns {Layout|LayoutResource} The created model instance with applied descriptor configuration
     *
     * @see ConfigurableMetadataProviderHelper.createModel
     */
    createModel(modelClass, resourceLoc, descriptor) {
      let resourceDescriptor = descriptor;
      if (!descriptor.skipBundles && this.isRunnableOnServer) {
        // As FYI, Dynamic Layout paths defined here
        // https://confluence.oraclecorp.com/confluence/pages/viewpage.action?spaceKey=ABCS&title=Layout+Editor+for+BOSS
        //
        // metadata-rules needs to know if it is a BOSS rule or not, because the V2 translations bundles have
        // different location and naming semantics.
        // eslint-disable-next-line max-len
        // See https://confluence.oraclecorp.com/confluence/display/ABCS/VBRT+V2+Translations+Structure%2C+Configuration+and+Expressions#VBRTV2TranslationsStructure,ConfigurationandExpressions-TranslationBundleDirectories
        // For Standard BOs, bundle location is relative to dynamicLayouts/self (all extension BOs share it)
        // For BOSS BOs, bundle location is relative to the BO's module (all module BOs share it), and
        // defaultBundleResourceName is prefixed with the moduleName
        // e.g.
        // vx/extId/dynamicLayouts/self/modules/moduleName/boName/
        // vx/extId/dynamicLayouts/self/modules/moduleName/resources/translations/moduleName-metadata-rules-i18n.js
        // vs
        // vx/extId/dynamicLayouts/self/boName/
        // vx/extId/dynamicLayouts/self/resources/translations/metadata-rules-i18n.js
        // (Note that we do not know if <extId>__ will be prefixed at this point in construction)
        //
        // Pass i18n information to the modelClass, for its loadTranslationBundles() configuration.
        const moduleName = this.getModulePath(); // Use the camelCase'd moduleName
        const modulePath = `${Constants.DefaultPaths.MODULES}${moduleName}/`;

        // If the resource is within the modules path, provide an i18n location.
        // e.g. field-templates-overlay is directly within dynamicLayouts/self/, but
        // not within dynamicLayouts/self/modules/moduleName/
        if (resourceLoc.indexOf(modulePath) !== -1) {
          resourceDescriptor = Object.assign(descriptor, {
            i18n: {
              location: `${this.getLayoutRoot()}${modulePath}`,
            },
            defaultBundleResourceName: `${moduleName}-${descriptor.defaultBundleResourceName}`,
          });
        }
      }

      return super.createModel(modelClass, resourceLoc, resourceDescriptor);
    }
  }

  return ServiceMetadataProviderHelper;
});

